import { router, usePathname, type Href } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { ROUTES } from '@/constants/routes';
import {
  confirmationDenialReasonFor,
  useAbility,
  type AbilityAction,
  type AbilitySubject,
} from '@/hooks/useAbility';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { buildAuthPathWithRedirect } from '@/hooks/useRedirectPath';
import { communityShowAuthModalAtom } from '@/services/globalState';

/**
 * Gates a control on the server's ability for that exact action, and sends a
 * reader who may not take it to the one step that would let them:
 *
 * - a guest gets the sign-in modal,
 * - an account still confirming its details goes to that screen, carrying the
 *   current path as `redirect_path` so confirming brings them back,
 * - an account that needs NPI verification gets the verification modal.
 *
 * The modal itself is `AbilityGateModal`, mounted once at the root, so a
 * screen gating a control does not render one of its own.
 *
 * The decision follows the reason the server gave for this action, not the
 * account's overall state. A denial for any other reason, such as a feature
 * gate, stays silent even for an account that is also unconfirmed: none of
 * those screens could lift it. So does the moment before the current user has
 * loaded, when an account has no rules yet and a confirmed reader would
 * otherwise be shown verification they do not need. The one account with no
 * rules that is still sent to verification is a sign-up whose NPI step is
 * pending, since it has no server account to load rules for yet.
 *
 * `guard` returns whether the action may go ahead, so a handler can bail on
 * `false`. `requestAccess` is the redirect on its own, for gates that have
 * already decided the reader is restricted.
 */
export function useAbilityGate() {
  const ability = useAbility();

  const { isUnauthenticated, needsNpiVerification, needsAccountDetailsConfirmation } =
    useAuthStatus();

  const setShowAuthModal = useSetAtom(communityShowAuthModalAtom);

  const pathname = usePathname();

  const requestAccess = useCallback(() => {
    if (needsAccountDetailsConfirmation) {
      router.push(buildAuthPathWithRedirect(ROUTES.CONFIRM_ACCOUNT_DETAILS, pathname) as Href);

      return;
    }

    setShowAuthModal(true);
  }, [needsAccountDetailsConfirmation, pathname, setShowAuthModal]);

  const guard = useCallback(
    (action: AbilityAction, subject: AbilitySubject): boolean => {
      if (ability.can(action, subject)) {
        return true;
      }

      const canVerificationHelp =
        isUnauthenticated ||
        confirmationDenialReasonFor(ability, action, subject) !== undefined ||
        (needsNpiVerification && ability.rules.length === 0);

      if (canVerificationHelp) {
        requestAccess();
      }

      return false;
    },
    [ability, isUnauthenticated, needsNpiVerification, requestAccess]
  );

  return { guard, requestAccess };
}
